"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MCNAccount = void 0;
const chain_1 = require("../../chain");
const utils_1 = require("../../utils");
const operation_1 = require("../operation");
const transaction_1 = require("../transaction");
const account_1 = require("./account");
const evm_1 = require("./evm");
const jvm_1 = require("./jvm");
const platform_1 = require("./platform");
class MCNAccount {
    wallet;
    chainAccounts = new Map();
    jvmAccount;
    crossManager;
    executingChains = [];
    constructor(provider, wallet) {
        this.wallet = wallet;
        const jvmAccount = new jvm_1.JVMAccount(provider, wallet);
        this.addAccount(jvmAccount);
        this.jvmAccount = jvmAccount;
        this.addAccount(new platform_1.PlatformAccount(provider, wallet));
        for (const chainId in provider.jevmApi) {
            this.addAccount(new evm_1.EVMAccount(provider, chainId, wallet));
        }
        this.crossManager = new transaction_1.CrossManager(provider, wallet);
    }
    addAccount(account) {
        this.chainAccounts.set(account.chain.id, account);
    }
    getAccount(chainId) {
        if (!this.chainAccounts.has(chainId)) {
            throw new utils_1.AccountError(`there is no account available for the chain with id: ${chainId}`);
        }
        return this.chainAccounts.get(chainId);
    }
    getTotalAssetValue(asset) {
        return asset.getAssetValue(this.getTotalAmount(asset.assetId));
    }
    getTotalAmount(assetId) {
        let totalAmount = BigInt(0);
        for (const [, account] of this.chainAccounts) {
            totalAmount += account.getBalance(assetId).getValue();
        }
        return totalAmount;
    }
    getTotalLockedAssetValue(asset) {
        return asset.getAssetValue(this.getTotalLockedAmount(asset.assetId));
    }
    getTotalLockedAmount(assetId) {
        let totalAmount = BigInt(0);
        for (const [, account] of this.chainAccounts) {
            if (account.type === account_1.AccountType.Utxo) {
                totalAmount += account.getLockedBalance(assetId).getValue();
            }
        }
        return totalAmount;
    }
    getTotalStakeableAssetValue(asset) {
        return asset.getAssetValue(this.getTotalStakeableAmount(asset.assetId));
    }
    getTotalStakeableAmount(assetId) {
        let totalAmount = BigInt(0);
        for (const [, account] of this.chainAccounts) {
            if (account.chain.vm.id === chain_1.PLATFORMVM_ID) {
                totalAmount += account.getStakeableBalance(assetId).getValue();
            }
        }
        return totalAmount;
    }
    async fetchUnfinishedJuneDepositOperations() {
        return await this.crossManager.fetchUnfinishedDepositOperations();
    }
    async fetchUnfinishedCrossOperations() {
        return await this.crossManager.fetchUnfinishedCrossOperations();
    }
    async estimate(operation) {
        if (operation.type === operation_1.NetworkOperationType.Cross) {
            const crossOperation = operation;
            // JVM balance might be needed for proxy and is mandatory if so before estimation
            await this.jvmAccount.refreshBalances();
            return await this.crossManager.estimateCrossOperation(crossOperation, this);
        }
        if (operation.type === operation_1.NetworkOperationType.CrossResume) {
            return await this.crossManager.estimateCrossResumeOperation(operation, this);
        }
        if (operation.type === operation_1.NetworkOperationType.DepositResume) {
            return await this.crossManager.estimateDepositResumeOperation(operation);
        }
        if (operation.range !== operation_1.NetworkOperationRange.Chain) {
            throw new utils_1.AccountError(`unsupported operation ${operation.type} for range: ${operation.range}`);
        }
        const chainOperation = operation;
        const account = this.getAccount(chainOperation.chain.id);
        // current utxo txs builders/helpers require utxos ids to mount transactions
        // until this is changed we need to fetch utxos prior to operation estimation
        if (account.type === account_1.AccountType.Utxo) {
            await account.refreshBalances();
        }
        return await account.estimate(chainOperation);
    }
    async execute(summary, skipVerifications = false) {
        const executable = summary.getExecutable();
        if (!skipVerifications && this.verifyChains(summary.getChains()).length > 0) {
            executable.status = operation_1.NetworkOperationStatus.Error;
            throw new utils_1.AccountError('an operation is already being executed on a chain');
        }
        if (!skipVerifications && (await this.verifySpendings(summary)).length > 0) {
            executable.status = operation_1.NetworkOperationStatus.Error;
            throw new utils_1.AccountError(`missing funds to perform operation: ${summary.operation.type}`);
        }
        executable.status = operation_1.NetworkOperationStatus.Executing;
        const range = summary.operation.range;
        if (summary.operation.type === operation_1.NetworkOperationType.CrossResume) {
            const resumeSummary = summary;
            await this.executeOperation(summary, this.crossManager.executeCrossResumeOperation(resumeSummary, this.getAccount(resumeSummary.chain.id)));
        }
        else if (summary.operation.type === operation_1.NetworkOperationType.DepositResume) {
            const resumeSummary = summary;
            await this.executeOperation(summary, this.crossManager.executeDepositResumeOperation(resumeSummary, this.getAccount(resumeSummary.chain.id)));
        }
        else if (range === operation_1.NetworkOperationRange.Chain) {
            const chainSummary = summary;
            const account = this.getAccount(chainSummary.chain.id);
            await this.executeOperation(summary, account.execute(chainSummary));
        }
        else if (range === operation_1.NetworkOperationRange.Supernet) {
            await this.executeOperation(summary, this.executeSupernetOperation(summary));
        }
        else {
            throw new utils_1.AccountError(`unsupported operation range: ${range}`);
        }
    }
    async executeSupernetOperation(summary) {
        // cross operation is currently the only supernet operation available
        // verifications are done in it so keep it like that until newer features are added
        const operation = summary.operation.type;
        if (operation === operation_1.NetworkOperationType.Cross) {
            const crossSummary = summary;
            await this.executeOperation(crossSummary, this.crossManager.executeCrossOperation(crossSummary, this));
        }
        else {
            throw new utils_1.AccountError(`unsupported supernet operation: ${operation}`);
        }
    }
    async executeOperation(summary, execution) {
        let error;
        const executable = summary.getExecutable();
        await execution.then(() => {
            // the only case it is not executing is if an error happened in that case we do not change it
            if (executable.status === operation_1.NetworkOperationStatus.Executing) {
                executable.status = operation_1.NetworkOperationStatus.Done;
            }
        }, (err) => {
            error = err;
            // only set as error if it has not been handled in the execution layer
            if (executable.status === operation_1.NetworkOperationStatus.Executing) {
                executable.status = operation_1.NetworkOperationStatus.Error;
            }
        });
        if (error === undefined) {
            // unlock the chains
            this.executingChains = [];
            return;
        }
        // most of the operations require to refetch balances but error could have cancelled it
        // try to restore a proper state by fetching them all. In the case of errors in more
        // complex such as those with a range higher than Chain we fetch it everywhere
        if (summary.operation.range !== operation_1.NetworkOperationRange.Chain) {
            for (const chain of summary.getChains()) {
                const chainAccount = this.getAccount(chain.id);
                await chainAccount.fetchBalances(chain.getRegisteredAssets());
            }
        }
        else {
            const operation = summary.operation;
            await this.getAccount(operation.chain.id).fetchBalances(summary.getAssets().values());
        }
        this.executingChains = [];
        throw error;
    }
    async verifySpendings(summary) {
        const spendings = (0, utils_1.sortSpendings)(summary.spendings);
        const promises = [];
        for (const spending of spendings.values()) {
            const assetId = spending.assetId;
            const account = this.getAccount(spending.chain.id);
            if (account.getAmount(assetId) < spending.amount || account.balances.get(assetId).shouldUpdate()) {
                promises.push(account.fetchBalance(assetId));
            }
        }
        await Promise.all(promises);
        const faulty = [];
        const isStakingOperation = summary.operation.type === operation_1.NetworkOperationType.ValidatePrimary ||
            summary.operation.type === operation_1.NetworkOperationType.DelegatePrimary;
        for (const spending of spendings.values()) {
            const account = this.getAccount(spending.chain.id);
            const amount = isStakingOperation
                ? account.getStakeableAmount(spending.assetId)
                : account.getAmount(spending.assetId);
            if (spending.amount > amount) {
                faulty.push(spending);
            }
        }
        return faulty;
    }
    verifyChains(chains) {
        const faulty = [];
        for (const chain of chains) {
            if (this.executingChains.includes(chain.id)) {
                faulty.push(chain.id);
            }
        }
        return faulty;
    }
}
exports.MCNAccount = MCNAccount;
